// FINAL CHAT V2 -- finds the tutor's figures in a reply (upstream 0a347ba,
// design A15). Upstream releases each figure as one normalized block,
// "\n```figure\n<one-line JSON spec>\n```\n", inside the reply text (and in
// the SSE stream only once the block is complete).

const FIGURE_BLOCK = /(^|\n)[ \t]*```figure[ \t]*\r?\n([\s\S]*?)\r?\n[ \t]*```[ \t]*(?=\r?\n|$)/g
// A figure block that has opened but not closed yet (defensive: upstream
// never streams half a block, but half a block must never show as code).
const OPEN_FIGURE_AT_END = /(^|\n)[ \t]*```figure[\s\S]*$/

/**
 * Splits a reply into text and figure parts, in order. A figure whose body
 * is not a JSON object still becomes a figure part (it then shows its
 * fallback), never raw JSON.
 * @param {string} source
 * @returns {({type:'text', text:string}|{type:'figure', spec:Object})[]}
 */
export function splitFigureBlocks(source) {
  if (typeof source !== 'string' || source === '') return []
  const parts = []
  let last = 0
  FIGURE_BLOCK.lastIndex = 0
  let match
  while ((match = FIGURE_BLOCK.exec(source)) !== null) {
    const start = match.index + match[1].length
    if (start > last)
      parts.push({ type: 'text', text: source.slice(last, start) })
    let spec
    try {
      spec = JSON.parse(match[2])
    } catch (err) {
      spec = null
    }
    parts.push({
      type: 'figure',
      spec:
        spec && typeof spec === 'object' && !Array.isArray(spec)
          ? spec
          : { kind: 'invalid', alt: '' },
    })
    last = FIGURE_BLOCK.lastIndex
  }
  let tail = source.slice(last)
  tail = tail.replace(OPEN_FIGURE_AT_END, '$1')
  if (tail.trim() || !parts.length) parts.push({ type: 'text', text: tail })
  return parts
}

// Key-order-independent identity of a spec, ignoring the service-added URL.
function canonical(value) {
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`
  if (value && typeof value === 'object') {
    return `{${Object.keys(value)
      .filter(key => key !== 'imageUrl')
      .sort()
      .map(key => `${JSON.stringify(key)}:${canonical(value[key])}`)
      .join(',')}}`
  }
  return JSON.stringify(value)
}

/**
 * The stored spec matching a block from the text (it may carry `imageUrl`),
 * or the block's own spec.
 * @param {Object} spec
 * @param {Object[]} figures
 * @returns {Object}
 */
export function resolveFigure(spec, figures) {
  if (!Array.isArray(figures) || !figures.length) return spec
  const key = canonical(spec)
  const stored = figures.find(figure => canonical(figure) === key)
  return stored || spec
}
